import { useState, useEffect } from "react";

import { useKeyUp, useBreakpointRange, constants } from "@newsela/angelou";
import FocusLock from "react-focus-lock";
import { FocusOn } from "react-focus-on";

import { ArticleLevel } from "@/types/models/ArticleLevel.interface";
import { Assignment } from "@/types/models/Assignment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import ActivityContainer from "@/view/ActivityContainer";
import contentTypes from "@/view/constants/contentTypes";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import quizOmissionReasons from "@/view/constants/quizOmissionReasons";
import videoPlayerEvents from "@/view/constants/videoPlayerEvents";
import useActivityPanel from "@/view/utils/useActivityPanel";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import useFocusManager from "@/view/utils/useFocusManager";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useSetupVideoEventListener from "@/view/utils/useSetupVideoEventListener";
import useVideoEventBus from "@/view/utils/useVideoEventBus";
import { IMetadata } from "@/view/utils/decodableContent";
import ActivityPanelTab from "./ActivityPanelTab";
import {
  panelCss,
  containerCss,
  focusContainerCss,
  wrapperCss,
} from "./ActivityPanel.style";

const { zeroToXs, mdToLg, lgToInfinity } =
  constants.breakpoints.BreakpointRanges;

interface ActivityPanelProps {
  assignments: Assignment[];
  assignmentsLoading: boolean;
  assignModalTriggerRef: React.Ref<HTMLButtonElement> | null;
  bannerMode: {
    canUseBannerMode: boolean;
    isBannerModeActive: boolean;
    toggleBannerMode: () => void;
  };
  content: {
    content_type: keyof typeof contentTypes;
    data: {
      is_lite?: boolean;
      article_levels: ArticleLevel[];
      quiz_omission_reason: keyof typeof quizOmissionReasons | null;
    };
    id: string;
    is_lite: boolean;
  };
  contentId: string;
  contentMetadata: IMetadata[];
  contentType: typeof contentTypes;
  currentAssignment: Assignment;
  currentAssignmentId: string;
  currentLevel: ArticleLevel;
  isBannerModeActive: boolean;
  isLevelLocked: boolean;
  isSelectedAssignmentLoading: boolean;
  fetchAssignment: (arg: string) => void;
  latestAssignment: Assignment | undefined;
  legacyArticleLevelData: object;
  assignModalProps: object;
  quizOmissionReason: typeof quizOmissionReasons;
  selectedAssignment: Assignment;
  studentId: number;
  toggleVocabularySupport: (a: boolean) => void;
  mobileActionsToolbarDropdownRef: React.Ref<HTMLButtonElement> | null;
  unlockLevel: () => void;
  user: {
    id: number | string;
    unique_id: string;
  };
  userProperties: UserProperties;
}

const ActivityPanel = ({
  assignments,
  assignmentsLoading,
  content,
  contentMetadata,
  contentId,
  contentType,
  currentLevel,
  isBannerModeActive,
  isSelectedAssignmentLoading,
  fetchAssignment,
  latestAssignment,
  legacyArticleLevelData,
  quizOmissionReason,
  selectedAssignment,
  studentId,
  unlockLevel,
  user,
  userProperties,
}: ActivityPanelProps) => {
  const { isActivityPanelExpanded, setIsActivityPanelExpanded } =
    useActivityPanel();
  const [inProgressQuizzes, updateinProgressQuizzes] = useState({});
  const [inProgressPowerWords, updateinProgressPowerWords] = useState({});
  const [activeVideoActivityLabel, setActiveVideoActivityLabel] = useState("");
  const { draftAnnotation } = useAnnotationsState();
  const { isVideoPresentModeActive, isPresentModeActive } =
    usePresentModeContext();
  const { VIEW_PAGE_EVENT, ANNOTATION_EVENT } = videoPlayerEvents;
  const { sendVideoPlayerEvent } = useVideoEventBus();

  const breakpointRange = useBreakpointRange();
  const isSmallerScreen =
    breakpointRange !== mdToLg && breakpointRange !== lgToInfinity;
  const isSmallestScreen = breakpointRange === zeroToXs;
  const { isStudent } = userProperties;

  const onEsc = () => {
    if (isSmallerScreen) {
      setIsActivityPanelExpanded(false);
    }
  };

  useKeyUp("escape", onEsc, {
    dependencyArray: [breakpointRange],
  });

  const { focusElement } = useFocusManager();

  const handleActivateEvent = (e: MouseEvent) => {
    if (e.type === "quiz") {
      focusElement(focusElementRefKeys.activityPanelBackButton);
      setActiveVideoActivityLabel("Quiz question");
    }
  };

  useSetupVideoEventListener(VIEW_PAGE_EVENT.RESET_ACTIVITY_PANEL_CONTENTS);
  useSetupVideoEventListener(ANNOTATION_EVENT.ACTIVATE, handleActivateEvent);

  useEffect(() => {
    if (draftAnnotation) {
      setIsActivityPanelExpanded(true);
    }
  }, [JSON.stringify(draftAnnotation), isActivityPanelExpanded]);

  const handleActivityTabClick = () => {
    setIsActivityPanelExpanded(true);
    sendVideoPlayerEvent(VIEW_PAGE_EVENT.RESET_ACTIVITY_PANEL_CONTENTS);
  };

  const activityPanelContents = (
    // Adding a tabIndex here because FocusOn and FocusLock will for some reason not update focus when an input is clicked, causing
    // the page to scroll back to whatever was focused on initially (the back button). Adding a div and tabIndex will force the
    // container and not the close button to be focused initally, and when clicking an input the focus will naturally go to the input
    <div tabIndex={0} aria-label="Activity panel">
      <ActivityContainer
        activeVideoActivityLabel={activeVideoActivityLabel}
        assignments={assignments}
        assignmentsLoading={assignmentsLoading}
        content={content}
        contentMetadata={contentMetadata}
        contentId={contentId}
        contentType={contentType}
        currentLevel={currentLevel}
        fetchAssignment={fetchAssignment}
        inProgressPowerWords={inProgressPowerWords}
        inProgressQuizzes={inProgressQuizzes}
        isBannerModeActive={isBannerModeActive}
        isPresentModeActive={isPresentModeActive}
        isSelectedAssignmentLoading={isSelectedAssignmentLoading}
        latestAssignment={latestAssignment}
        legacyArticleLevelData={legacyArticleLevelData}
        quizOmissionReason={quizOmissionReason}
        selectedAssignment={selectedAssignment}
        setActiveVideoActivityLabel={setActiveVideoActivityLabel}
        unlockLevel={unlockLevel}
        updateinProgressPowerWords={updateinProgressPowerWords}
        updateinProgressQuizzes={updateinProgressQuizzes}
        user={user}
        studentId={studentId}
        userProperties={userProperties}
      />
    </div>
  );

  const getContentsWithFocusLockComponent = (isPanelVisible: boolean) => {
    // FocusOn prevents screen readers from reading hidden elements within in the activities panel when view on mobile devices.
    if (isSmallestScreen) {
      // There is a bug in FocusOn that prevents click events from being registered
      // when the panel is hidden. So we wrap panel content in FocusOn only when
      // the panel is opened, and we actually need trap the focus within the panel
      return isPanelVisible ? (
        <FocusOn scrollLock={false} css={focusContainerCss}>
          {activityPanelContents}
        </FocusOn>
      ) : (
        activityPanelContents
      );
    }
    // FocusLock maintains focus in the panel, but allows scrolling on the page when small/medium screens are used.
    return (
      <FocusLock
        disabled={!isSmallerScreen}
        returnFocus
        css={focusContainerCss}
      >
        {activityPanelContents}
      </FocusLock>
    );
  };

  return (
    <div data-qa-selector="activity-panel" css={wrapperCss}>
      <div>
        <ActivityPanelTab
          isStudent={isStudent}
          onClick={handleActivityTabClick}
        />
      </div>
      <div css={panelCss(isActivityPanelExpanded, isVideoPresentModeActive)}>
        <div css={containerCss}>
          {getContentsWithFocusLockComponent(isActivityPanelExpanded)}
        </div>
      </div>
    </div>
  );
};

export default ActivityPanel;
